<template>
  <div class="manual-review">
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">人工审核</h2>
        <p class="page-subtitle">处理风控系统触发的奖金审核申请</p>
      </div>
      <el-button :icon="Refresh" @click="loadList">刷新</el-button>
    </div>

    <!-- 筛选栏 -->
    <el-card shadow="never" class="filter-card">
      <el-form inline>
        <el-form-item label="审核状态">
          <el-select v-model="filterStatus" style="width:130px" @change="loadList">
            <el-option label="待审核" value="pending" />
            <el-option label="已通过" value="approved" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item label="触发类型">
          <el-select v-model="filterType" style="width:180px" clearable @change="loadList">
            <el-option label="全部" value="" />
            <el-option label="创建者自获奖" value="creator_self_win" />
            <el-option label="每日转账超限" value="daily_limit_exceeded" />
            <el-option label="单笔大额预警" value="single_prize_alert" />
            <el-option label="参与人数不足" value="min_participants_not_met" />
          </el-select>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 待审核数量提示 -->
    <el-alert
      v-if="pendingCount > 0 && filterStatus !== 'pending'"
      :title="`当前有 ${pendingCount} 条记录待审核`"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom:16px"
    />

    <!-- 列表 -->
    <el-card shadow="never">
      <el-table
        :data="list"
        v-loading="loading"
        style="width:100%"
        empty-text="暂无审核记录"
        row-class-name="review-row"
      >
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="触发类型" width="160">
          <template #default="{ row }">
            <el-tag :type="typeTagMap[row.type]?.color || 'info'" size="small">
              {{ typeTagMap[row.type]?.label || row.type }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="涉及用户" width="140">
          <template #default="{ row }">
            <span>{{ row.user_nickname || `ID:${row.user_id}` }}</span>
          </template>
        </el-table-column>
        <el-table-column label="涉及活动" min-width="160">
          <template #default="{ row }">
            <span>{{ row.activity_title || (row.activity_id ? `ID:${row.activity_id}` : '—') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="金额(元)" width="110" align="right">
          <template #default="{ row }">
            <span class="amount-text">¥ {{ Number(row.amount).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="detail" label="风控说明" min-width="200" show-overflow-tooltip />
        <el-table-column prop="created_at" label="触发时间" width="170" />
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="statusTagMap[row.status]?.color" size="small">
              {{ statusTagMap[row.status]?.label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <template v-if="row.status === 'pending'">
              <el-button size="small" type="success" @click="openApprove(row)">通过</el-button>
              <el-button size="small" type="danger" @click="openReject(row)">拒绝</el-button>
            </template>
            <template v-else>
              <el-button size="small" @click="showDetail(row)">详情</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrap">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="total"
          :page-sizes="[10, 20, 50]"
          layout="total, sizes, prev, pager, next"
          @size-change="loadList"
          @current-change="loadList"
        />
      </div>
    </el-card>

    <!-- 审核通过 Dialog -->
    <el-dialog v-model="approveDialogVisible" title="审核通过确认" width="440px">
      <p>确认对以下记录 <strong>审核通过</strong>，审核通过后系统将在下次补发扫描时自动发放奖金。</p>
      <el-descriptions :column="1" border size="small" style="margin:12px 0">
        <el-descriptions-item label="用户">{{ currentRow?.user_nickname }}</el-descriptions-item>
        <el-descriptions-item label="金额">¥ {{ Number(currentRow?.amount).toFixed(2) }}</el-descriptions-item>
        <el-descriptions-item label="说明">{{ currentRow?.detail }}</el-descriptions-item>
      </el-descriptions>
      <el-form>
        <el-form-item label="审核备注">
          <el-input v-model="reviewNote" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="approveDialogVisible = false">取消</el-button>
        <el-button type="success" :loading="actionLoading" @click="doApprove">确认通过</el-button>
      </template>
    </el-dialog>

    <!-- 审核拒绝 Dialog -->
    <el-dialog v-model="rejectDialogVisible" title="审核拒绝确认" width="440px">
      <p>确认对以下记录 <strong>审核拒绝</strong>，拒绝后奖金将不予发放，系统会通知用户。</p>
      <el-descriptions :column="1" border size="small" style="margin:12px 0">
        <el-descriptions-item label="用户">{{ currentRow?.user_nickname }}</el-descriptions-item>
        <el-descriptions-item label="金额">¥ {{ Number(currentRow?.amount).toFixed(2) }}</el-descriptions-item>
        <el-descriptions-item label="说明">{{ currentRow?.detail }}</el-descriptions-item>
      </el-descriptions>
      <el-form>
        <el-form-item label="拒绝原因">
          <el-input v-model="reviewNote" type="textarea" :rows="2" placeholder="请填写拒绝原因（将通知用户）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="actionLoading" @click="doReject">确认拒绝</el-button>
      </template>
    </el-dialog>

    <!-- 详情 Dialog -->
    <el-dialog v-model="detailDialogVisible" title="审核详情" width="480px">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="触发类型">
          <el-tag :type="typeTagMap[currentRow?.type]?.color" size="small">
            {{ typeTagMap[currentRow?.type]?.label }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="涉及用户">{{ currentRow?.user_nickname }}</el-descriptions-item>
        <el-descriptions-item label="涉及活动">{{ currentRow?.activity_title || '—' }}</el-descriptions-item>
        <el-descriptions-item label="金额">¥ {{ Number(currentRow?.amount).toFixed(2) }}</el-descriptions-item>
        <el-descriptions-item label="风控说明">{{ currentRow?.detail }}</el-descriptions-item>
        <el-descriptions-item label="触发时间">{{ currentRow?.created_at }}</el-descriptions-item>
        <el-descriptions-item label="审核状态">
          <el-tag :type="statusTagMap[currentRow?.status]?.color" size="small">
            {{ statusTagMap[currentRow?.status]?.label }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="审核时间">{{ currentRow?.reviewed_at || '—' }}</el-descriptions-item>
        <el-descriptions-item label="审核备注">{{ currentRow?.review_note || '—' }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import api from '@/utils/api'

export default {
  name: 'ManualReview',
  setup() {
    const loading = ref(false)
    const list = ref([])
    const total = ref(0)
    const pendingCount = ref(0)
    const page = ref(1)
    const pageSize = ref(20)
    const filterStatus = ref('pending')
    const filterType = ref('')

    const approveDialogVisible = ref(false)
    const rejectDialogVisible = ref(false)
    const detailDialogVisible = ref(false)
    const actionLoading = ref(false)
    const currentRow = ref(null)
    const reviewNote = ref('')

    const typeTagMap = {
      creator_self_win:       { label: '创建者自获奖', color: 'danger' },
      daily_limit_exceeded:   { label: '每日转账超限', color: 'warning' },
      single_prize_alert:     { label: '单笔大额预警', color: 'warning' },
      min_participants_not_met: { label: '参与人数不足', color: 'info' }
    }
    const statusTagMap = {
      pending:  { label: '待审核', color: 'warning' },
      approved: { label: '已通过', color: 'success' },
      rejected: { label: '已拒绝', color: 'danger' }
    }

    const loadList = async () => {
      loading.value = true
      try {
        const res = await api.getManualReviews({ status: filterStatus.value, type: filterType.value || undefined, page: page.value, pageSize: pageSize.value })
        list.value = res.data?.list || []
        total.value = res.data?.total || 0
        pendingCount.value = res.data?.pendingCount || 0
      } catch (e) {
        ElMessage.error('加载审核列表失败')
      } finally {
        loading.value = false
      }
    }

    const openApprove = (row) => { currentRow.value = row; reviewNote.value = ''; approveDialogVisible.value = true }
    const openReject  = (row) => { currentRow.value = row; reviewNote.value = ''; rejectDialogVisible.value = true }
    const showDetail  = (row) => { currentRow.value = row; detailDialogVisible.value = true }

    const doApprove = async () => {
      actionLoading.value = true
      try {
        await api.approveManualReview(currentRow.value.id, { note: reviewNote.value })
        ElMessage.success('已审核通过')
        approveDialogVisible.value = false
        loadList()
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || '操作失败')
      } finally {
        actionLoading.value = false
      }
    }

    const doReject = async () => {
      actionLoading.value = true
      try {
        await api.rejectManualReview(currentRow.value.id, { note: reviewNote.value })
        ElMessage.success('已拒绝')
        rejectDialogVisible.value = false
        loadList()
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || '操作失败')
      } finally {
        actionLoading.value = false
      }
    }

    onMounted(loadList)

    return {
      loading, list, total, pendingCount, page, pageSize,
      filterStatus, filterType,
      approveDialogVisible, rejectDialogVisible, detailDialogVisible,
      actionLoading, currentRow, reviewNote,
      typeTagMap, statusTagMap,
      loadList, openApprove, openReject, showDetail, doApprove, doReject,
      Refresh
    }
  }
}
</script>

<style scoped>
.manual-review { padding: 20px; }
.page-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 20px;
}
.page-title { margin: 0; font-size: 20px; color: #303133; }
.page-subtitle { margin: 4px 0 0; color: #909399; font-size: 13px; }
.filter-card { margin-bottom: 16px; }
.filter-card :deep(.el-card__body) { padding: 16px 20px 0; }
.pagination-wrap { display: flex; justify-content: flex-end; margin-top: 16px; }
.amount-text { color: #e6a23c; font-weight: 600; }
</style>
